import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { registerLogoutHandler, attachAxiosInterceptor } from '@/utils/apiFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme system — hoisted to module scope so the reference
// is stable across AuthProvider re-renders and can be
// safely included in the context-value useMemo deps.
// ceo_instruction_ref:
//   authcontext_value_memoise_22jun2026
const THEMES = {
  warm_parchment: { bg: '#F8F4F1', text: '#1A2744', card: '#FFFFFF' },
  soft_grey: { bg: '#F0F0F0', text: '#1A2744', card: '#FFFFFF' },
  cool_white: { bg: '#F8F9FA', text: '#1A2744', card: '#FFFFFF' },
  dark_mode: { bg: '#0F1B2D', text: '#F8F4F1', card: '#1A2744' },
  sage: { bg: '#EFF2EE', text: '#1A2744', card: '#FFFFFF' },
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('rk_token'));
  const [loading, setLoading] = useState(true);
  // Dedicated impersonation loading state — strictly
  // separate from the main `loading` state so VIEW AS
  // token swaps never cause ProtectedRoute to render
  // the app-splash screen. ceo_instruction_ref:
  //   impersonation_no_loading_flash_31may2026
  const [impersonating, setImpersonating] = useState(false);
  const [impersonation, setImpersonationState] = useState(() => {
    try {
      const saved = localStorage.getItem('rk_impersonation');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const initDone = useRef(false);
  // Ref flag — set TRUE by setImpersonation() /
  // exitImpersonation() across the token swap so any
  // downstream useEffect that watches `token` can detect
  // the swap is an impersonation event (not a fresh
  // session) and skip its own loading cycle.
  // ceo_instruction_ref:
  //   impersonation_no_loading_flash_31may2026
  const isImpersonationSwap = useRef(false);

  const getHeaders = useCallback(() => {
    const t = token || localStorage.getItem('rk_token');
    if (t) return { Authorization: `Bearer ${t}` };
    return {};
  }, [token]);

  const setSession = useCallback((t, u) => {
    localStorage.setItem('rk_token', t);
    if (u?.region) localStorage.setItem('rk_user_region', u.region);
    setToken(t);
    setUser(u);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem('rk_token');
    setToken(null);
    setUser(null);
  }, []);

  const loginWithToken = useCallback((t, u) => {
    setSession(t, u);
  }, [setSession]);

  // Run ONCE on mount — check auth state
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    // Skip if Google OAuth callback in progress
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const initAuth = async () => {
      const savedToken = localStorage.getItem('rk_token');
      const savedImpersonation = localStorage.getItem('rk_impersonation');

      if (savedToken) {
        try {
          const res = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          const userData = res.data;
          // Preserve demo flags from the /auth/me response
          setUser(userData);
          setToken(savedToken);
          // Save region for cart pricing
          if (userData?.region) localStorage.setItem('rk_user_region', userData.region);

          // Restore impersonation state if it was active
          if (savedImpersonation) {
            try {
              const imp = JSON.parse(savedImpersonation);
              setImpersonationState({
                token: savedToken,
                user: res.data,
                mode: imp.mode,
                originalToken: imp.originalToken,
                originalUser: imp.originalUser,
                impersonatedByName: imp.impersonatedByName,
              });
            } catch { localStorage.removeItem('rk_impersonation'); }
          }

          setLoading(false);
          return;
        } catch {
          // Token expired or invalid — clear impersonation too
          localStorage.removeItem('rk_token');
          localStorage.removeItem('rk_impersonation');
        }
      }

      // Try refresh token
      const refreshToken = localStorage.getItem('rk_refresh_token');
      if (refreshToken) {
        try {
          const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
          localStorage.setItem('rk_token', res.data.token);
          setToken(res.data.token);
          setUser(res.data.user);
          setLoading(false);
          return;
        } catch {
          localStorage.removeItem('rk_refresh_token');
        }
      }

      // No valid session
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password, rememberDevice = false) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });

    // 2FA required — return signal to login page
    if (res.data.requires_2fa) {
      return { requires_2fa: true, email };
    }

    const { token: t, user: u } = res.data;
    setSession(t, u);

    if (rememberDevice) {
      try {
        const deviceInfo = navigator.userAgent.substring(0, 100);
        const refreshRes = await axios.post(`${API}/auth/refresh-token`,
          { device_info: deviceInfo },
          { headers: { Authorization: `Bearer ${t}` } }
        );
        localStorage.setItem('rk_refresh_token', refreshRes.data.refresh_token);
      } catch (e) { console.warn('Could not create refresh token', e); }
    }
    return u;
  }, [setSession]);

  const verify2FA = useCallback(async (email, code) => {
    const res = await axios.post(`${API}/auth/verify-2fa`, { email, code });
    const { token: t, user: u } = res.data;
    setSession(t, u);
    return u;
  }, [setSession]);

  const register = useCallback(async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    const { token: t, user: u } = res.data;
    setSession(t, u);
    return u;
  }, [setSession]);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const handleGoogleCallback = useCallback(async (sessionId) => {
    const res = await axios.post(`${API}/auth/session`, { session_id: sessionId });
    const { token: t, user: u } = res.data;
    setSession(t, u);
    return u;
  }, [setSession]);

  const loginWithBiometric = useCallback(async (email) => {
    const optionsRes = await axios.post(`${API}/auth/webauthn/login/options`, { email });
    const options = optionsRes.data;

    options.challenge = _base64URLToBuffer(options.challenge);
    if (options.allowCredentials) {
      options.allowCredentials = options.allowCredentials.map(c => ({
        ...c, id: _base64URLToBuffer(c.id)
      }));
    }

    const assertion = await navigator.credentials.get({ publicKey: options });

    const credential = {
      id: assertion.id,
      rawId: _bufferToBase64URL(assertion.rawId),
      response: {
        authenticatorData: _bufferToBase64URL(assertion.response.authenticatorData),
        clientDataJSON: _bufferToBase64URL(assertion.response.clientDataJSON),
        signature: _bufferToBase64URL(assertion.response.signature),
        userHandle: assertion.response.userHandle ? _bufferToBase64URL(assertion.response.userHandle) : null,
      },
      type: assertion.type,
      authenticatorAttachment: assertion.authenticatorAttachment,
    };

    const verifyRes = await axios.post(`${API}/auth/webauthn/login/verify`, {
      email, credential
    });
    const { token: t, user: u } = verifyRes.data;
    setSession(t, u);

    // Auto-remember biometric device
    try {
      const deviceInfo = navigator.userAgent.substring(0, 100);
      const refreshRes = await axios.post(`${API}/auth/refresh-token`,
        { device_info: deviceInfo },
        { headers: { Authorization: `Bearer ${t}` } }
      );
      localStorage.setItem('rk_refresh_token', refreshRes.data.refresh_token);
    } catch (e) { console.warn('Could not create refresh token', e); }

    return u;
  }, [setSession]);

  const registerBiometric = useCallback(async (deviceName = 'My Device') => {
    if (!token) throw new Error('Must be logged in');

    const optionsRes = await axios.post(`${API}/auth/webauthn/register/options`, {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const options = optionsRes.data;

    options.challenge = _base64URLToBuffer(options.challenge);
    options.user.id = _base64URLToBuffer(options.user.id);
    if (options.excludeCredentials) {
      options.excludeCredentials = options.excludeCredentials.map(c => ({
        ...c, id: _base64URLToBuffer(c.id)
      }));
    }

    const credential = await navigator.credentials.create({ publicKey: options });

    const body = {
      id: credential.id,
      rawId: _bufferToBase64URL(credential.rawId),
      response: {
        attestationObject: _bufferToBase64URL(credential.response.attestationObject),
        clientDataJSON: _bufferToBase64URL(credential.response.clientDataJSON),
      },
      type: credential.type,
      authenticatorAttachment: credential.authenticatorAttachment,
      device_name: deviceName,
    };

    const verifyRes = await axios.post(`${API}/auth/webauthn/register/verify`, body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return verifyRes.data;
  }, [token]);

  const setImpersonation = useCallback((data) => {
    if (data) {
      // Mark the swap so downstream token-watching code
      // can skip its loading cycle. ceo_instruction_ref:
      //   impersonation_no_loading_flash_31may2026
      isImpersonationSwap.current = true;
      setImpersonating(true);
      // Save original token before overwriting localStorage
      const currentToken = localStorage.getItem('rk_token');
      data.originalToken = data.originalToken || currentToken;

      setImpersonationState(data);
      setToken(data.token);
      setUser(data.user);

      // Update localStorage so getHeaders() and API calls use the impersonated token
      localStorage.setItem('rk_token', data.token);
      localStorage.setItem('rk_impersonation', JSON.stringify({
        originalToken: data.originalToken,
        originalUser: data.originalUser,
        mode: data.mode,
        impersonatedByName: data.impersonatedByName,
      }));
      // Option β (30 May 2026,
      //   ceo_instruction_ref:
      //     recent_strip_impersonation_bleed_30may2026):
      // Clear the founder's prior browsing history so the RECENT
      // strip reflects only the impersonated user's own visits,
      // never the founder's pre-impersonation paths
      // (e.g. /dashboard/salon-registry).
      localStorage.removeItem('rk_recent_visited');
      // Clear the swap markers. The main `loading` state
      // was NEVER touched in this function.
      setImpersonating(false);
      isImpersonationSwap.current = false;
    }
  }, []);

  const exitImpersonation = useCallback(() => {
    if (impersonation) {
      // Mark the swap so downstream token-watching code
      // can skip its loading cycle. ceo_instruction_ref:
      //   impersonation_no_loading_flash_31may2026
      isImpersonationSwap.current = true;
      setImpersonating(true);
      setToken(impersonation.originalToken);
      setUser(impersonation.originalUser);
      localStorage.setItem('rk_token', impersonation.originalToken);
      localStorage.removeItem('rk_impersonation');
      // Option β — clear again on exit so the founder doesn't
      // inherit the impersonated user's RECENT strip on return.
      localStorage.removeItem('rk_recent_visited');
      setImpersonationState(null);
      // Clear the swap markers. The main `loading` state
      // was NEVER touched in this function.
      setImpersonating(false);
      isImpersonationSwap.current = false;
    }
  }, [impersonation]);

  const logout = useCallback(() => {
    localStorage.removeItem('rk_token');
    localStorage.removeItem('rk_refresh_token');
    localStorage.removeItem('rk_impersonation');
    setToken(null);
    setUser(null);
    setImpersonationState(null);
  }, []);

  // ── apiFetch 401 hook (P0-1, 17 May 2026) ──
  // Register the logout handler so the global apiFetch
  // wrapper can clear React state on 401 before
  // redirecting to /login?reason=session_expired.
  // Also attach an axios response interceptor so legacy
  // axios call sites benefit from the same behaviour.
  // ceo_instruction_ref:
  //   p0_1_apifetch_wrapper_stage_17may2026
  useEffect(() => {
    registerLogoutHandler(logout);
    const detach = attachAxiosInterceptor(axios);
    return () => {
      registerLogoutHandler(null);
      if (typeof detach === 'function') detach();
    };
    // logout is stable for the lifetime of the provider
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  const setRegion = useCallback(async (region) => {
    const t = token || localStorage.getItem('rk_token');
    if (!t) return;
    try {
      await axios.post(`${API}/auth/set-region`, { region }, { headers: { Authorization: `Bearer ${t}` } });
      setUser(prev => prev ? { ...prev, selected_region: region } : prev);
    } catch (e) { console.error('Failed to set region', e); }
  }, [token]);

  const refreshUser = useCallback(async () => {
    // Skip refresh during impersonation to avoid reverting to original user
    if (impersonation) return impersonation.user;
    const t = token || localStorage.getItem('rk_token');
    if (!t) return null;
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [impersonation, token]);

  // Theme system
  const applyTheme = useCallback((themeName) => {
    const t = THEMES[themeName] || THEMES.warm_parchment;
    document.documentElement.style.setProperty('--rv-bg', t.bg);
    document.documentElement.style.setProperty('--rv-text', t.text);
    document.documentElement.style.setProperty('--rv-card', t.card);
    document.body.style.backgroundColor = t.bg;
    if (themeName === 'dark_mode') {
      document.body.classList.add('rv-dark');
    } else {
      document.body.classList.remove('rv-dark');
    }
  }, []);

  useEffect(() => {
    const theme = user?.theme || 'warm_parchment';
    applyTheme(theme);
  }, [user?.theme, applyTheme]);

  const setTheme = useCallback(async (themeName) => {
    applyTheme(themeName);
    setUser(prev => prev ? { ...prev, theme: themeName } : prev);
    try {
      await axios.post(`${API}/auth/set-theme`, { theme: themeName }, { headers: getHeaders() });
    } catch {}
  }, [applyTheme, getHeaders]);

  // Memoised context value — stable object reference
  // across provider re-renders unless one of the listed
  // fields actually changes. Prevents every useAuth()
  // consumer (UKDistributors, MySalonInvoicesPage, etc.)
  // from re-rendering on every provider state update.
  // ceo_instruction_ref:
  //   authcontext_value_memoise_22jun2026
  const contextValue = useMemo(() => ({
    user, token, loading, impersonation, impersonating,
    login, register, loginWithGoogle, handleGoogleCallback, verify2FA,
    loginWithBiometric, registerBiometric, loginWithToken,
    logout, getHeaders, updateUser, refreshUser, setRegion,
    setImpersonation, exitImpersonation,
    setTheme, THEMES
  }), [
    user, token, loading, impersonation, impersonating,
    login, register, loginWithGoogle, handleGoogleCallback, verify2FA,
    loginWithBiometric, registerBiometric, loginWithToken,
    logout, getHeaders, updateUser, refreshUser, setRegion,
    setImpersonation, exitImpersonation,
    setTheme, THEMES,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

function _base64URLToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function _bufferToBase64URL(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
